import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  type DocumentData,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  writeBatch,
  where
} from 'firebase/firestore';
import { db } from '../firebase';
import { DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS, normalizeDiscussionNotificationSettings } from './notifications';
import { discussionTagDisplayOrder, discussionTagDisplayRow, normalizeDiscussionCategoryId } from './discussionCategories';
import type {
  Company,
  UserProfile,
  Tag,
  DiscussionTopic,
  DiscussionPollCompletion,
  DiscussionPollQuestion,
  DiscussionPollResultSummary,
  DiscussionAttachment,
  DiscussionReply,
  Event,
  EventReply,
  Conference,
  FeedbackAnswerValue,
  FeedbackForm,
  FeedbackResponse,
  Session,
  SessionAttendancePlan,
  SessionDiscussionReply,
  DiscussionNotificationSettings,
  NotificationImmediateChannel,
  NotificationMode,
  PushSubscriptionRecord
} from './types';

export const col = {
  companies: () => collection(db, 'companies'),
  profiles: () => collection(db, 'profiles'),
  profileNotificationSettings: (profileId: string) => collection(db, 'profiles', profileId, 'notificationSettings'),
  profilePushSubscriptions: (profileId: string) => collection(db, 'profiles', profileId, 'pushSubscriptions'),
  tags: () => collection(db, 'tags'),
  topics: () => collection(db, 'discussionTopics'),
  topicLikes: (topicId: string) => collection(db, 'discussionTopics', topicId, 'likes'),
  replies: (topicId: string) => collection(db, 'discussionTopics', topicId, 'replies'),
  topicAttachments: (topicId: string) => collection(db, 'discussionTopics', topicId, 'attachments'),
  replyAttachments: (topicId: string, replyId: string) =>
    collection(db, 'discussionTopics', topicId, 'replies', replyId, 'attachments'),
  replyLikes: (topicId: string, replyId: string) =>
    collection(db, 'discussionTopics', topicId, 'replies', replyId, 'likes'),
  pollQuestions: (topicId: string) => collection(db, 'discussionTopics', topicId, 'pollQuestions'),
  pollCompletions: (topicId: string) => collection(db, 'discussionTopics', topicId, 'pollCompletions'),
  events: () => collection(db, 'events'),
  eventReplies: (eventId: string) => collection(db, 'events', eventId, 'replies'),
  conferences: () => collection(db, 'conferences'),
  sessions: (conferenceId: string) => collection(db, 'conferences', conferenceId, 'sessions'),
  sessionDiscussionReplies: (conferenceId: string, sessionId: string) =>
    collection(db, 'conferences', conferenceId, 'sessions', sessionId, 'discussionReplies'),
  sessionDiscussionReplyLikes: (conferenceId: string, sessionId: string, replyId: string) =>
    collection(db, 'conferences', conferenceId, 'sessions', sessionId, 'discussionReplies', replyId, 'likes'),
  feedbackForms: () => collection(db, 'feedbackForms'),
  feedbackResponses: (formId: string) => collection(db, 'feedbackForms', formId, 'responses')
};

export const DEFAULT_CONFERENCE_ID = '2026-Conference';

const now = () => Date.now();
const discussionNotificationSettingsRef = (profileId: string) => doc(db, 'profiles', profileId, 'notificationSettings', 'discussion');
const pushSubscriptionRef = (profileId: string, subscriptionId: string) =>
  doc(db, 'profiles', profileId, 'pushSubscriptions', subscriptionId);
const topicLikeRef = (topicId: string, profileId: string) =>
  doc(db, 'discussionTopics', topicId, 'likes', profileId);
const replyLikeRef = (topicId: string, replyId: string, profileId: string) =>
  doc(db, 'discussionTopics', topicId, 'replies', replyId, 'likes', profileId);
const pollCompletionRef = (topicId: string, profileId: string) =>
  doc(db, 'discussionTopics', topicId, 'pollCompletions', profileId);
const pollResultSummaryRef = (topicId: string, questionId: string) =>
  doc(db, 'discussionTopics', topicId, 'pollQuestions', questionId, 'results', 'summary');
const sessionAttendancePlanRef = (conferenceId: string, sessionId: string, profileId: string) =>
  doc(db, 'conferences', conferenceId, 'sessions', sessionId, 'attendance', profileId);
const sessionDiscussionReplyLikeRef = (conferenceId: string, sessionId: string, replyId: string, profileId: string) =>
  doc(db, 'conferences', conferenceId, 'sessions', sessionId, 'discussionReplies', replyId, 'likes', profileId);
const feedbackFormRef = (formId: string) => doc(db, 'feedbackForms', formId);
const feedbackResponseRef = (formId: string, profileId: string) =>
  doc(db, 'feedbackForms', formId, 'responses', profileId);
const qrLanderConfigRef = () => doc(db, 'siteConfig', 'qrlander');

function isReportedOrHidden(value: { moderationStatus?: unknown }) {
  const status = String(value.moderationStatus || '').trim();
  return status === 'reported' || status === 'hidden';
}

export type DiscussionTopicsPageCursor = QueryDocumentSnapshot<DocumentData> | null;

export interface DiscussionTopicsPageResult {
  items: DiscussionTopic[];
  nextCursor: DiscussionTopicsPageCursor;
  hasMore: boolean;
}

export interface DiscussionReplySearchItem {
  reply: DiscussionReply;
  topicId: string;
}

function topicActivityMs(topic: DiscussionTopic) {
  return Number(topic.lastActivityAt || topic.updatedAt || topic.createdAt || 0);
}

function normalizedDiscussionTagIds(tagIds?: string[]) {
  return Array.from(new Set((tagIds ?? []).map(normalizeDiscussionCategoryId).filter(Boolean))).slice(0, 30);
}

function topicMatchesAnyTag(topic: DiscussionTopic, tagIds: string[]) {
  if (!tagIds.length) return true;
  const topicTagIds = (topic.tagIds ?? []).map(normalizeDiscussionCategoryId);
  return tagIds.some((tagId) => topicTagIds.includes(tagId));
}

function topicFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData>): DiscussionTopic {
  return { id: snapshot.id, ...(snapshot.data() as Omit<DiscussionTopic, 'id'>) };
}

function discussionTopicFilterConstraints(tagIds: string[]): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];
  if (tagIds.length === 1) {
    constraints.push(where('tagIds', 'array-contains', tagIds[0]));
  } else if (tagIds.length > 1) {
    constraints.push(where('tagIds', 'array-contains-any', tagIds));
  }
  return constraints;
}

function discussionTopicQueryConstraints(tagIds: string[], cursor?: DiscussionTopicsPageCursor): QueryConstraint[] {
  const constraints = discussionTopicFilterConstraints(tagIds);
  constraints.push(orderBy('lastActivityAt', 'desc'));
  if (cursor) constraints.push(startAfter(cursor));
  return constraints;
}

export async function listTags(scope?: Tag['scope'], activeOnly: boolean = false): Promise<Tag[]> {
  const q = scope ? query(col.tags(), where('scope', '==', scope)) : query(col.tags());
  const snap = await getDocs(q);

  const items = snap.docs.map((d) => {
    const data = d.data() as any;
    // Backwards compatible: tags missing isActive are treated as active
    const isActive = data.isActive !== false;
    return { id: d.id, ...data, isActive } as any as Tag;
  });

  // Client-side sorting (stable; avoids extra Firestore indexes)
  // Active first, then display row/order when present, then A-Z by name.
  items.sort((a: any, b: any) => {
    const aActive = (a as any).isActive !== false;
    const bActive = (b as any).isActive !== false;
    if (aActive !== bActive) return aActive ? -1 : 1;

    const rowDiff = discussionTagDisplayRow(a) - discussionTagDisplayRow(b);
    if (rowDiff !== 0) return rowDiff;

    const orderDiff = discussionTagDisplayOrder(a) - discussionTagDisplayOrder(b);
    if (orderDiff !== 0) return orderDiff;

    const an = String((a as any).name ?? '').toLowerCase();
    const bn = String((b as any).name ?? '').toLowerCase();
    if (an !== bn) return an.localeCompare(bn);

    return String((a as any).slug ?? (a as any).id).localeCompare(String((b as any).slug ?? (b as any).id));
  });

  return activeOnly ? items.filter((t: any) => t.isActive !== false) : items;
}

function slugify(input: string) {
  return input
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Create a discussion tag (prototype/admin use).
 * Uses a stable document id to avoid duplicates.
 */
export async function createDiscussionTag(
  name: string,
  createdByProfileId?: string,
  placement?: { displayRow?: number; displayOrder?: number; tooltip?: string }
): Promise<string> {
  const slug = slugify(name);
  if (!slug) throw new Error('Tag name is invalid');
  const tooltip = placement?.tooltip?.trim();

  const id = slug;
  const ref = doc(db, 'tags', id);

  const existing = await getDoc(ref);
  if (existing.exists()) throw new Error('Tag already exists');

  await setDoc(ref, {
    scope: 'discussion',
    name: name.trim(),
    slug,
    isActive: true,
    ...(tooltip ? { tooltip } : {}),
    ...(Number.isFinite(placement?.displayRow) ? { displayRow: placement?.displayRow } : {}),
    ...(Number.isFinite(placement?.displayOrder) ? { displayOrder: placement?.displayOrder } : {}),
    createdAt: serverTimestamp(),
    createdByProfileId: createdByProfileId ?? null
  });

  return id;
}

export async function renameTag(tagId: string, newName: string) {
  await updateDoc(doc(db, 'tags', tagId), {
    name: newName.trim(),
    updatedAt: serverTimestamp()
  });
}

export async function setTagActive(tagId: string, isActive: boolean) {
  await updateDoc(doc(db, 'tags', tagId), {
    isActive,
    updatedAt: serverTimestamp()
  });
}

export async function updateTagPlacement(tagId: string, displayRow: number, displayOrder: number) {
  await updateDoc(doc(db, 'tags', tagId), {
    displayRow,
    displayOrder,
    updatedAt: serverTimestamp()
  });
}

export async function updateTagTooltip(tagId: string, tooltip: string) {
  const trimmedTooltip = tooltip.trim();
  await updateDoc(doc(db, 'tags', tagId), {
    tooltip: trimmedTooltip || deleteField(),
    updatedAt: serverTimestamp()
  });
}

export async function listCompanies(): Promise<Company[]> {
  const snap = await getDocs(query(col.companies(), orderBy('name', 'asc')));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Company, 'id'>) }));
}

export async function listProfiles(): Promise<UserProfile[]> {
  const snap = await getDocs(query(col.profiles(), orderBy('displayName', 'asc')));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<UserProfile, 'id'>) }));
}

export function subscribeDiscussionNotificationSettings(
  profileId: string,
  onData: (settings: DiscussionNotificationSettings) => void
) {
  return onSnapshot(discussionNotificationSettingsRef(profileId), (snap) => {
    if (!snap.exists()) {
      onData(DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS);
      return;
    }
    onData(normalizeDiscussionNotificationSettings(snap.data()));
  });
}

export async function setDiscussionNotificationAllMode(profileId: string, mode: NotificationMode) {
  await setDoc(
    discussionNotificationSettingsRef(profileId),
    {
      allDiscussionsMode: mode,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}

export async function setDiscussionNotificationAllImmediateChannel(
  profileId: string,
  channel: NotificationImmediateChannel
) {
  await setDoc(
    discussionNotificationSettingsRef(profileId),
    {
      allDiscussionsImmediateChannel: channel,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}

export async function setDiscussionNotificationTagMode(
  profileId: string,
  tagId: string,
  mode: NotificationMode | null
) {
  const normalizedTagId = normalizeDiscussionCategoryId(tagId);
  const legacyTagId = `discussion:${normalizedTagId}`;
  const next: Record<string, unknown> = {
    tagModes: {
      [normalizedTagId]: mode ?? deleteField(),
      ...(legacyTagId !== normalizedTagId ? { [legacyTagId]: deleteField() } : {})
    },
    updatedAt: serverTimestamp()
  };
  if (mode !== 'immediate') {
    next.tagImmediateChannels = {
      [normalizedTagId]: deleteField(),
      ...(legacyTagId !== normalizedTagId ? { [legacyTagId]: deleteField() } : {})
    };
  }

  await setDoc(
    discussionNotificationSettingsRef(profileId),
    next,
    { merge: true }
  );
}

export async function setDiscussionNotificationTagImmediateChannel(
  profileId: string,
  tagId: string,
  channel: NotificationImmediateChannel | null
) {
  await setDoc(
    discussionNotificationSettingsRef(profileId),
    {
      tagImmediateChannels: {
        [tagId]: channel ?? deleteField()
      },
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}

export async function setDiscussionNotificationTopicMode(
  profileId: string,
  topicId: string,
  mode: NotificationMode | null
) {
  const next: Record<string, unknown> = {
    topicModes: {
      [topicId]: mode ?? deleteField()
    },
    updatedAt: serverTimestamp()
  };
  if (mode !== 'immediate') {
    next.topicImmediateChannels = {
      [topicId]: deleteField()
    };
  }

  await setDoc(
    discussionNotificationSettingsRef(profileId),
    next,
    { merge: true }
  );
}

export async function setDiscussionNotificationTopicImmediateChannel(
  profileId: string,
  topicId: string,
  channel: NotificationImmediateChannel | null
) {
  await setDoc(
    discussionNotificationSettingsRef(profileId),
    {
      topicImmediateChannels: {
        [topicId]: channel ?? deleteField()
      },
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}

export async function setTopicReplyEmailNotifications(profileId: string, topicId: string, enabled: boolean) {
  await setDoc(
    discussionNotificationSettingsRef(profileId),
    {
      topicModes: {
        [topicId]: enabled ? 'immediate' : deleteField()
      },
      topicImmediateChannels: {
        [topicId]: enabled ? 'email' : deleteField()
      },
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}

export async function markDiscussionNotificationsSeen(profileId: string) {
  await setDoc(
    discussionNotificationSettingsRef(profileId),
    {
      lastViewedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
}

export function subscribePushSubscriptions(profileId: string, onData: (items: PushSubscriptionRecord[]) => void) {
  return onSnapshot(col.profilePushSubscriptions(profileId), (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<PushSubscriptionRecord, 'id'>) }));
    onData(items);
  });
}

export async function upsertPushSubscription(
  profileId: string,
  subscriptionId: string,
  input: Omit<PushSubscriptionRecord, 'id' | 'createdAt' | 'updatedAt' | 'lastSeenAt'>
) {
  await setDoc(
    pushSubscriptionRef(profileId, subscriptionId),
    {
      ...input,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastSeenAt: serverTimestamp()
    },
    { merge: true }
  );
}

export async function removePushSubscription(profileId: string, subscriptionId: string) {
  await deleteDoc(pushSubscriptionRef(profileId, subscriptionId));
}

export async function createTopic(input: {
  title: string;
  body: string;
  authorProfileId: string;
  authorDisplayName?: string;
  tagIds: string[];
  pollQuestions?: Array<{
    question: string;
    options: string[];
  }>;
}): Promise<string> {
  const ts = now();
  const pollQuestions = (input.pollQuestions ?? [])
    .map((question) => ({
      question: question.question.trim(),
      options: question.options.map((option) => option.trim()).filter(Boolean)
    }))
    .filter((question) => question.question && question.options.length >= 2)
    .slice(0, 5);

  const topic: Omit<DiscussionTopic, 'id'> = {
    title: input.title,
    body: input.body,
    authorProfileId: input.authorProfileId,
    authorDisplayName: input.authorDisplayName || '',
    tagIds: input.tagIds,
    ...(pollQuestions.length
      ? {
          hasPoll: true,
          poll: {
            createdByProfileId: input.authorProfileId,
            createdAt: ts,
            questionCount: pollQuestions.length,
            closedAt: null
          }
        }
      : {}),
    pinned: false,
    locked: false,
    subscribersCount: 0,
    moderationStatus: 'visible',
    lastActivityAt: ts,
    createdAt: ts,
    updatedAt: ts
  };

  if (!pollQuestions.length) {
    const ref = await addDoc(col.topics(), topic);
    return ref.id;
  }

  const ref = doc(col.topics());
  const batch = writeBatch(db);
  batch.set(ref, topic);

  pollQuestions.forEach((pollQuestion, questionIndex) => {
    const questionRef = doc(col.pollQuestions(ref.id), `q_${questionIndex + 1}`);
    batch.set(questionRef, {
      topicId: ref.id,
      question: pollQuestion.question,
      options: pollQuestion.options.slice(0, 8).map((label, optionIndex) => ({
        id: `o_${optionIndex + 1}`,
        label
      })),
      order: questionIndex,
      createdAt: ts,
      updatedAt: ts
    });
  });

  await batch.commit();
  return ref.id;
}

export function subscribeTopics(opts: { tagId?: string; onData: (items: DiscussionTopic[]) => void; take?: number }) {
  const base = opts.tagId
    ? query(col.topics(), where('tagIds', 'array-contains', opts.tagId), orderBy('lastActivityAt', 'desc'), limit(opts.take ?? 50))
    : query(col.topics(), orderBy('lastActivityAt', 'desc'), limit(opts.take ?? 50));

  return onSnapshot(base, (snap) => {
    const items = snap.docs
      .map((d) => ({ id: d.id, ...(d.data() as Omit<DiscussionTopic, 'id'>) }))
      .filter((topic) => !isReportedOrHidden(topic));
    opts.onData(items);
  });
}

export async function listDiscussionTopicsPage(opts: {
  tagIds?: string[];
  pageSize?: number;
  cursor?: DiscussionTopicsPageCursor;
  includePinned?: boolean;
}): Promise<DiscussionTopicsPageResult> {
  const pageSize = Math.max(1, Math.min(opts.pageSize ?? 10, 50));
  const tagIds = normalizedDiscussionTagIds(opts.tagIds);
  const constraints = discussionTopicQueryConstraints(tagIds, opts.cursor);
  const pageSnap = await getDocs(query(col.topics(), ...constraints, limit(pageSize + 1)));
  const pageDocs = pageSnap.docs.slice(0, pageSize);
  const pageItems = pageDocs
    .map(topicFromSnapshot)
    .filter((topic) => !isReportedOrHidden(topic));

  let pinnedItems: DiscussionTopic[] = [];
  if (!opts.cursor && opts.includePinned !== false) {
    const pinnedSnap = await getDocs(query(col.topics(), where('pinned', '==', true), limit(50)));
    pinnedItems = pinnedSnap.docs
      .map(topicFromSnapshot)
      .filter((topic) => !isReportedOrHidden(topic))
      .filter((topic) => topicMatchesAnyTag(topic, tagIds))
      .sort((a, b) => topicActivityMs(b) - topicActivityMs(a));
  }

  const byId = new Map<string, DiscussionTopic>();
  pinnedItems.forEach((topic) => byId.set(topic.id, topic));
  pageItems.forEach((topic) => {
    if (!byId.has(topic.id)) byId.set(topic.id, topic);
  });

  return {
    items: Array.from(byId.values()),
    nextCursor: pageDocs[pageDocs.length - 1] ?? null,
    hasMore: pageSnap.docs.length > pageSize
  };
}

export async function countDiscussionTopics(opts: { tagIds?: string[] } = {}): Promise<number> {
  const tagIds = normalizedDiscussionTagIds(opts.tagIds);
  const snap = await getCountFromServer(query(col.topics(), ...discussionTopicFilterConstraints(tagIds)));
  return snap.data().count;
}

export async function listDiscussionTopics(): Promise<DiscussionTopic[]> {
  const snap = await getDocs(query(col.topics(), orderBy('lastActivityAt', 'desc')));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<DiscussionTopic, 'id'>) }))
    .filter((topic) => !isReportedOrHidden(topic));
}

export async function listDiscussionRepliesForSearch(limitTo: number = 1000): Promise<DiscussionReplySearchItem[]> {
  let snap;
  try {
    snap = await getDocs(query(collectionGroup(db, 'replies'), orderBy('updatedAt', 'desc'), limit(limitTo)));
  } catch (error) {
    console.warn('Falling back to unordered reply search query.', error);
    snap = await getDocs(query(collectionGroup(db, 'replies'), limit(limitTo)));
  }

  return snap.docs
    .map((d) => {
      const reply = { id: d.id, ...(d.data() as Omit<DiscussionReply, 'id'>) };
      return {
        reply,
        topicId: String(reply.topicId || '')
      };
    })
    .filter((item) => item.topicId && !isReportedOrHidden(item.reply));
}

export async function getTopic(topicId: string): Promise<DiscussionTopic | null> {
  const snap = await getDoc(doc(db, 'discussionTopics', topicId));
  if (!snap.exists()) return null;
  const topic = { id: snap.id, ...(snap.data() as Omit<DiscussionTopic, 'id'>) };
  return isReportedOrHidden(topic) ? null : topic;
}

export function subscribePollQuestions(topicId: string, onData: (items: DiscussionPollQuestion[]) => void) {
  const q = query(col.pollQuestions(topicId), orderBy('order', 'asc'));
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DiscussionPollQuestion, 'id'>) }));
    onData(items);
  });
}

export function subscribePollCompletion(
  topicId: string,
  profileId: string,
  onData: (completion: DiscussionPollCompletion | null) => void
) {
  return onSnapshot(pollCompletionRef(topicId, profileId), (snap) => {
    if (!snap.exists()) {
      onData(null);
      return;
    }
    onData({ id: snap.id, ...(snap.data() as Omit<DiscussionPollCompletion, 'id'>) });
  });
}

export function subscribePollResultSummary(
  topicId: string,
  questionId: string,
  onData: (summary: DiscussionPollResultSummary | null) => void
) {
  return onSnapshot(pollResultSummaryRef(topicId, questionId), (snap) => {
    if (!snap.exists()) {
      onData(null);
      return;
    }
    onData({ id: snap.id, ...(snap.data() as Omit<DiscussionPollResultSummary, 'id'>) });
  });
}

export async function getEvent(eventId: string): Promise<Event | null> {
  const snap = await getDoc(doc(db, 'events', eventId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Event, 'id'>) };
}

export async function getConference(conferenceId: string): Promise<Conference | null> {
  const snap = await getDoc(doc(db, 'conferences', conferenceId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Conference, 'id'>) };
}

export async function getDefaultConference(): Promise<Conference | null> {
  const configuredConference = await getConference(DEFAULT_CONFERENCE_ID);
  if (configuredConference) return configuredConference;

  return (await listConferences())[0] ?? null;
}

export async function getConferenceSession(conferenceId: string, sessionId: string): Promise<Session | null> {
  const snap = await getDoc(doc(db, 'conferences', conferenceId, 'sessions', sessionId));
  if (!snap.exists()) return null;
  return { id: snap.id, conferenceId, ...(snap.data() as Omit<Session, 'id' | 'conferenceId'>) };
}

export async function getFeedbackForm(formId: string): Promise<FeedbackForm | null> {
  const snap = await getDoc(feedbackFormRef(formId));
  if (!snap.exists()) return null;

  const data = snap.data() as Omit<FeedbackForm, 'id'>;
  if (data.status !== 'active') return null;

  const questions = Array.isArray(data.questions)
    ? [...data.questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  return { id: snap.id, ...data, questions };
}

export async function getFeedbackResponse(formId: string, profileId: string): Promise<FeedbackResponse | null> {
  const snap = await getDoc(feedbackResponseRef(formId, profileId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<FeedbackResponse, 'id'>) };
}

export async function setFeedbackResponse(input: {
  formId: string;
  profileId: string;
  answers: Record<string, FeedbackAnswerValue>;
}) {
  const ref = feedbackResponseRef(input.formId, input.profileId);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    throw new Error('Feedback has already been submitted.');
  }

  await setDoc(
    ref,
    {
      formId: input.formId,
      profileId: input.profileId,
      answers: input.answers,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      submittedAt: serverTimestamp()
    }
  );
}

export async function getQrLanderTarget(): Promise<string | null> {
  const snap = await getDoc(qrLanderConfigRef());
  if (!snap.exists()) return null;
  const targetUrl = snap.data().targetUrl;
  return typeof targetUrl === 'string' ? targetUrl : null;
}

export function subscribeReplies(topicId: string, onData: (items: DiscussionReply[]) => void, onError?: () => void) {
  const q = query(col.replies(topicId), orderBy('createdAt', 'asc'));
  return onSnapshot(q, (snap) => {
    const items = snap.docs
      .map((d) => ({ id: d.id, ...(d.data() as Omit<DiscussionReply, 'id'>) }))
      .filter((reply) => !isReportedOrHidden(reply));
    onData(items);
  }, onError);
}

export function subscribeTopicAttachments(topicId: string, onData: (items: DiscussionAttachment[]) => void) {
  const q = query(col.topicAttachments(topicId), orderBy('createdAt', 'asc'));
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DiscussionAttachment, 'id'>) }));
    onData(items);
  });
}

export function subscribeReplyAttachments(
  topicId: string,
  replyId: string,
  onData: (items: DiscussionAttachment[]) => void
) {
  const q = query(col.replyAttachments(topicId, replyId), orderBy('createdAt', 'asc'));
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<DiscussionAttachment, 'id'>) }));
    onData(items);
  });
}

export function subscribeDiscussionReplyLiked(
  topicId: string,
  replyId: string,
  profileId: string,
  onData: (liked: boolean) => void
) {
  return onSnapshot(replyLikeRef(topicId, replyId, profileId), (snap) => {
    onData(snap.exists());
  });
}

export function subscribeDiscussionTopicLiked(
  topicId: string,
  profileId: string,
  onData: (liked: boolean) => void
) {
  return onSnapshot(topicLikeRef(topicId, profileId), (snap) => {
    onData(snap.exists());
  });
}

export function subscribeDiscussionTopicLikers(
  topicId: string,
  onData: (profileIds: string[]) => void,
  onError: () => void
) {
  return onSnapshot(col.topicLikes(topicId), (snap) => {
    onData(snap.docs.map((like) => like.id));
  }, onError);
}

export async function setDiscussionTopicLiked(input: {
  topicId: string;
  profileId: string;
  liked: boolean;
}) {
  const ref = topicLikeRef(input.topicId, input.profileId);
  if (input.liked) {
    await setDoc(ref, {
      topicId: input.topicId,
      profileId: input.profileId,
      createdAt: now()
    });
  } else {
    await deleteDoc(ref);
  }
}

export async function setDiscussionReplyLiked(input: {
  topicId: string;
  replyId: string;
  profileId: string;
  liked: boolean;
}) {
  const ref = replyLikeRef(input.topicId, input.replyId, input.profileId);
  if (input.liked) {
    await setDoc(ref, {
      topicId: input.topicId,
      replyId: input.replyId,
      profileId: input.profileId,
      createdAt: now()
    });
  } else {
    await deleteDoc(ref);
  }
}

export function subscribeEventReplies(eventId: string, onData: (items: EventReply[]) => void) {
  const q = query(col.eventReplies(eventId), orderBy('createdAt', 'asc'));
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<EventReply, 'id'>) }));
    onData(items);
  });
}

export function subscribeSessionDiscussionReplies(
  conferenceId: string,
  sessionId: string,
  onData: (items: SessionDiscussionReply[]) => void
) {
  const q = query(col.sessionDiscussionReplies(conferenceId, sessionId), orderBy('createdAt', 'asc'));
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<SessionDiscussionReply, 'id'>) }));
    onData(items);
  });
}

export function subscribeSessionDiscussionReplyLiked(
  conferenceId: string,
  sessionId: string,
  replyId: string,
  profileId: string,
  onData: (liked: boolean) => void
) {
  return onSnapshot(sessionDiscussionReplyLikeRef(conferenceId, sessionId, replyId, profileId), (snap) => {
    onData(snap.exists());
  });
}

export async function setSessionDiscussionReplyLiked(input: {
  conferenceId: string;
  sessionId: string;
  replyId: string;
  profileId: string;
  liked: boolean;
}) {
  const ref = sessionDiscussionReplyLikeRef(input.conferenceId, input.sessionId, input.replyId, input.profileId);
  if (input.liked) {
    await setDoc(ref, {
      conferenceId: input.conferenceId,
      sessionId: input.sessionId,
      replyId: input.replyId,
      profileId: input.profileId,
      createdAt: now()
    });
  } else {
    await deleteDoc(ref);
  }
}

export async function addReply(input: {
  topicId: string;
  parentReplyId?: string;
  body: string;
  authorProfileId: string;
  authorDisplayName?: string;
}): Promise<string> {
  const reply: Omit<DiscussionReply, 'id'> = {
    topicId: input.topicId,
    ...(input.parentReplyId ? { parentReplyId: input.parentReplyId } : {}),
    body: input.body,
    authorProfileId: input.authorProfileId,
    authorDisplayName: input.authorDisplayName || '',
    moderationStatus: 'visible',
    createdAt: now(),
    updatedAt: now()
  };

  const ref = await addDoc(col.replies(input.topicId), reply);
  await updateDoc(doc(db, 'discussionTopics', input.topicId), { lastActivityAt: now(), updatedAt: now() });
  return ref.id;
}

function formatTopicTagChangeMessage(addedTagNames: string[], removedTagNames: string[]) {
  const parts: string[] = [];
  if (addedTagNames.length) parts.push(`added ${addedTagNames.join(', ')}`);
  if (removedTagNames.length) parts.push(`removed ${removedTagNames.join(', ')}`);
  return `Updated categories: ${parts.join('; ')}.`;
}

export async function updateTopicTagsWithSystemReply(input: {
  topicId: string;
  authorProfileId: string;
  authorDisplayName?: string;
  tagIds: string[];
  addedTagNames: string[];
  removedTagNames: string[];
}): Promise<void> {
  if (!input.addedTagNames.length && !input.removedTagNames.length) return;

  const ts = now();
  const topicRef = doc(db, 'discussionTopics', input.topicId);
  const replyRef = doc(col.replies(input.topicId));
  const batch = writeBatch(db);

  batch.update(topicRef, {
    tagIds: input.tagIds,
    lastActivityAt: ts,
    updatedAt: ts
  });

  const reply: Omit<DiscussionReply, 'id'> = {
    topicId: input.topicId,
    body: formatTopicTagChangeMessage(input.addedTagNames, input.removedTagNames),
    authorProfileId: input.authorProfileId,
    authorDisplayName: input.authorDisplayName || '',
    systemAction: 'topic_tags_updated',
    createdAt: ts,
    updatedAt: ts
  };

  batch.set(replyRef, reply);
  await batch.commit();
}

export async function addEventReply(input: {
  eventId: string;
  parentReplyId?: string;
  body: string;
  authorProfileId: string;
  authorDisplayName?: string;
}): Promise<string> {
  const ts = now();
  const reply: Omit<EventReply, 'id'> = {
    eventId: input.eventId,
    ...(input.parentReplyId ? { parentReplyId: input.parentReplyId } : {}),
    body: input.body,
    authorProfileId: input.authorProfileId,
    authorDisplayName: input.authorDisplayName || '',
    createdAt: ts,
    updatedAt: ts
  };

  const ref = await addDoc(col.eventReplies(input.eventId), reply);
  await updateDoc(doc(db, 'events', input.eventId), { lastActivityAt: ts, updatedAt: ts });
  return ref.id;
}

export async function addSessionDiscussionReply(input: {
  conferenceId: string;
  sessionId: string;
  parentReplyId?: string;
  body: string;
  authorProfileId: string;
  authorDisplayName?: string;
}): Promise<string> {
  const ts = now();
  const reply: Omit<SessionDiscussionReply, 'id'> = {
    conferenceId: input.conferenceId,
    sessionId: input.sessionId,
    ...(input.parentReplyId ? { parentReplyId: input.parentReplyId } : {}),
    body: input.body,
    authorProfileId: input.authorProfileId,
    authorDisplayName: input.authorDisplayName || '',
    createdAt: ts,
    updatedAt: ts
  };

  const ref = await addDoc(col.sessionDiscussionReplies(input.conferenceId, input.sessionId), reply);
  return ref.id;
}

export function subscribeEvents(onData: (items: Event[]) => void) {
  const q = query(col.events(), where('endAt', '>=', now()), orderBy('endAt', 'asc'));
  return onSnapshot(q, (snap) => {
    const items = snap.docs
      .map((d) => ({ id: d.id, ...(d.data() as Omit<Event, 'id'>) }))
      .sort((a, b) => a.startAt - b.startAt);
    onData(items);
  });
}

export async function createEvent(input: Omit<Event, 'id'>): Promise<string> {
  const ref = await addDoc(col.events(), input);
  return ref.id;
}

export async function listConferences(): Promise<Conference[]> {
  const snap = await getDocs(query(col.conferences(), orderBy('startAt', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Conference, 'id'>) }));
}

export function subscribeConferenceSessions(
  conferenceId: string,
  onData: (items: Session[]) => void,
  onError?: (error: Error) => void
) {
  const q = query(col.sessions(conferenceId), orderBy('startAt', 'asc'));
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Session, 'id'>) }));
    onData(items);
  }, onError);
}

export async function listConferenceSessions(conferenceId: string): Promise<Session[]> {
  const snap = await getDocs(query(col.sessions(conferenceId), orderBy('startAt', 'asc')));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Session, 'id'>) }));
}

export async function createSession(conferenceId: string, input: Omit<Session, 'id' | 'conferenceId'>): Promise<string> {
  const ref = doc(col.sessions(conferenceId));
  const session: Session = { id: ref.id, conferenceId, ...input };
  await setDoc(ref, session);
  return ref.id;
}

export async function listSessionAttendancePlans(
  conferenceId: string,
  sessionIds: string[],
  profileId: string,
): Promise<SessionAttendancePlan[]> {
  const uniqueSessionIds = Array.from(new Set(sessionIds.filter(Boolean)));
  const snaps = await Promise.all(
    uniqueSessionIds.map((sessionId) => getDoc(sessionAttendancePlanRef(conferenceId, sessionId, profileId))),
  );

  return snaps
    .filter((snap) => snap.exists())
    .map((snap) => ({ id: snap.id, ...(snap.data() as Omit<SessionAttendancePlan, 'id'>) }));
}

export async function setSessionAttendancePlan(input: {
  conferenceId: string;
  sessionId: string;
  profileId: string;
  planning: boolean;
}) {
  const res = await fetch('/auth/conference/attendance', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });

  if (!res.ok) {
    let message = `Could not save attendance plan (${res.status})`;
    try {
      const payload = (await res.json()) as { error?: string };
      if (payload.error) message = payload.error;
    } catch {
      // Keep the status-based fallback.
    }
    throw new Error(message);
  }
}
